import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {NullAsDefault} from 'views/Shared'
import {announcement_gets_Api} from "../../../apis";
import WorkingAppContext from "../../Shared/contexts/WorkingAppContext";
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";
import {Button, Image, Modal} from "antd";
import CRUD from "../../Shared/enums/CRUD";
import {BsInboxesFill} from "react-icons/bs";
import {formatSimplifyDate} from "../../../helpers/dateUtils";
import {useLocation} from "react-use";
import {useAnnouncementForm} from "../../AppLocals/Announcement/useAnnouncementForm";
import DepartmentFilteredHeader from "../components/DepartmentFilteredHeader";
import _ from "lodash";
import {DoubleValueItem} from 'components/common/DoubleValueItem'
import usePaginationHelper, {createPagerKey} from "../../AppLocals/hooks/usePaginationHelper";
import {NoneStickyMeta} from "../../AppLocals/EntityList";
import {useRouteSearchParam} from "../../../envs/_current/history";
import Toggle from "../../../components/Toggle";
import {HiLink, HiViewfinderCircle} from "react-icons/hi2";
import {ImagePlaceholder} from "../../../conts/imageBase64";
import {TYPE_COLORS} from "../../Organization/components/OrganizationAnnouncementItem";
import {LazyPagination} from "../../../modules/asyncCache/components/LazyPagination";
import {TbEdit, TbTrash} from "react-icons/tb";
import {isAnnouncementExpired} from "../../AppLocals/Announcement/announcementHelpers";


const WrapperDetailItem = ({title, children}) => {
    const t = useTranslate()

    return (
        <div className="flex flex-col gap-1">
            <span className="font-medium text-color-200 tracking-wide">
                {t(title)}
            </span>
            {children}
        </div>
    )
}


export const renderOrganizationAnnouncement = ({t, handleAction = NullAsDefault, actionMenu = false}) => (item) => {

    const {type_code, type_label, title, content, photo, time_open, time_close, shared_url, created, organization, group} = item ?? {}

    const colors = TYPE_COLORS[type_code] || TYPE_COLORS.DEFAULT
    const canManage = actionMenu && (!!group?.edit || !!organization?.edit)
    // Badge cạnh loại thông báo để phân biệt với đang/sắp diễn ra - xem isAnnouncementExpired
    // (không có time_close thì coi như hết hạn cuối ngày time_open).
    const isExpired = isAnnouncementExpired({time_open, time_close, created})

    return (
        <Toggle>
            {(isToggle, toggle) => (
                <React.Fragment>
                    <div
                        key={item.id}
                        className="rounded-2xl border border-color-50 shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer background"
                        onClick={toggle}>

                        {/* Photo */}
                        {photo && (
                            <Image
                                src={photo || ImagePlaceholder}
                                fallback={ImagePlaceholder}
                                style={{maxHeight: 160}}
                                className="object-cover w-full"
                                wrapperClassName="w-full"
                                preview={false}
                            />
                        )}

                        {/* Header — title/type luôn full-width, không chia chỗ với action để tránh vỡ layout khi title dài */}
                        <div className="px-4 py-3 border-b border-color-50">
                            <div className="font-bold text-sm text-color-000 leading-snug">{title}</div>
                            {(type_label || isExpired) && (
                                <div className="mt-1 flex items-center gap-1.5">
                                    {type_label && (
                                        <span className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-xs font-medium"
                                              style={{color: colors?.label || undefined}}>
                                            {type_label}
                                        </span>
                                    )}
                                    {isExpired && (
                                        <span className="px-2 py-0.5 rounded-full bg-color-100 border border-color-100 text-color-400 text-xs font-medium">
                                            {t('expired')}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="px-4 py-3">
                            <div className="text-sm text-color-100 whitespace-pre-line leading-relaxed line-clamp-3">
                                {content}
                            </div>
                            {!!shared_url && (
                                <a
                                    target="_blank"
                                    href={shared_url}
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    className="mt-2 block text-xs text-indigo-400 italic truncate no-underline hover:underline">
                                    {shared_url}
                                </a>
                            )}
                        </div>

                        {/* Footer — info bên trái (flex-1, tự truncate/co giãn), action admin bên phải (cố định) cùng 1 hàng */}
                        <div className="px-4 py-2 border-t border-color-50 background-50 flex items-center gap-3">
                            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                {group?.title && (
                                    <span className="text-xs font-semibold text-color-200 truncate">
                                        {group.title}
                                    </span>
                                )}
                                <span className="text-xs text-color-400 italic flex-shrink-0">
                                    {formatSimplifyDate(created, 'fullTime')}
                                </span>
                            </div>
                            {canManage && (
                                <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        onClick={() => handleAction(CRUD.UPDATE, {...item, organization})}
                                        style={{minWidth: 36, minHeight: 36}}
                                        className="flex flex-center rounded-lg text-indigo-500 hover:bg-indigo-50 transition-colors">
                                        <TbEdit size={15}/>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleAction(CRUD.DELETE, {...item, organization})}
                                        style={{minWidth: 36, minHeight: 36}}
                                        className="flex flex-center rounded-lg text-rose-500 hover:bg-rose-50 transition-colors">
                                        <TbTrash size={15}/>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    {isToggle && (
                        <Modal
                            width={600}
                            footer={null}
                            centered={true}
                            open={isToggle}
                            destroyOnHidden={true}
                            onCancel={() => toggle(false)}>
                            <div className="flex flex-col gap-6">
                                <div className="text-center font-bold text-color-000 tracking-wide">
                                    {t('announcements')}
                                </div>
                                <div className="flex flex-col gap-3">
                                    <WrapperDetailItem title="title">
                                        <span className="ml-3 font-bold text-lg text-gray-700 tracking-wide">
                                            {title}
                                        </span>
                                        {type_label && (
                                            <span
                                                className="ml-3 font-semibold text-sm text-blue-700 italic tracking-wide">
                                                {t(type_label)}
                                            </span>
                                        )}
                                    </WrapperDetailItem>
                                    <WrapperDetailItem title="content">
                                        {photo && (
                                            <Image
                                                src={photo || ImagePlaceholder}
                                                fallback={ImagePlaceholder}
                                                // style={{height: 150}}
                                                className="flex flex-center object-cover rounded-xl"
                                                wrapperClassName="ml-3 w-full object-cover rounded-xl"
                                                preview={{
                                                    mask: (
                                                        <div className="w-full h-full relative">
                                                            <HiViewfinderCircle
                                                                size={16} style={{top: 10, right: 10}}
                                                                className="absolute text-white opacity-85 hover:opacity-100 custom-img-view-icon"
                                                            />
                                                        </div>
                                                    ),
                                                }}
                                            />
                                        )}
                                        <div className="ml-3 text-color-300 tracking-wider whitespace-pre-line">
                                            {content}
                                        </div>
                                        {(time_open || time_close) && (
                                            <DoubleValueItem
                                                showIcon={true}
                                                labelFirst={t('from date')}
                                                valueFirst={formatSimplifyDate(time_open, 'medium')}
                                                labelSecond={t('to date')}
                                                valueSecond={formatSimplifyDate(time_close, 'medium')}
                                                className="ml-3 text-sm"
                                            />
                                        )}
                                        {shared_url && (
                                            <div className="flex items-center gap-1">
                                                <HiLink size={12} className="text-blue-500"/>
                                                <a
                                                    href={shared_url} target="_blank"
                                                    className="flex-1 text-sm text-blue-600 italic no-underline max-lines-1">
                                                    {shared_url}
                                                </a>
                                            </div>
                                        )}
                                    </WrapperDetailItem>
                                    <WrapperDetailItem title="send at">
                                        <span className="ml-3 text-color-300 tracking-wider italic">
                                            {formatSimplifyDate(created, 'fullTime')}
                                        </span>
                                    </WrapperDetailItem>
                                </div>
                                <div className="text-right">
                                    <span
                                        onClick={() => toggle(false)}
                                        className="text-color-400 hover:text-color-100 cursor-pointer">
                                        {t('close')}
                                    </span>
                                </div>
                            </div>
                        </Modal>
                    )}
                </React.Fragment>
            )}
        </Toggle>

    )
}


export const OrganizationActiveAnnouncements = ({}) => {
    const t = useTranslate()
    const {activeOrganization} = useContext(WorkingAppContext)

    if (_.isEmpty(activeOrganization)) return null

    return (
        <LazyPagination
            sample={5}
            apiInfo={
                announcement_gets_Api
            }
            values={{
                owner_type: 'organization',
                owner_id: activeOrganization?.id,
                status: 1
            }}
            renderItem={
                renderOrganizationAnnouncement({
                    t,
                    actionMenu: false
                })
            }
            renderWrap={(children, __, total) => !!total && (
                <div className="p-3 flex flex-col gap-3 bg-gray-200 shadow-lg rounded-3xl">
                    <div className="mb-2 flex flex-col items-center">
                        <h2 className="text-xl font-semibold text-blue-700 tracking-wider">
                            {t('announcements')}
                        </h2>
                        <div className="w-16 h-1 bg-blue-500 mx-auto mt-1 rounded"/>
                    </div>
                    <div className="space-y-3">
                        {children}
                    </div>
                </div>
            )}
        />
    )

}

const OrganizationAnnouncements = ({
                                       title,
                                       filter_actions = {
                                           sortBy: 'sort-by',
                                           keyword: 'keyword',
                                       },
                                   }) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const location = useLocation()
    const {
        activeProfile = null,
        groups = [],
        selectedDepartmentId,
        selectedDepartment,
        activeOrganization
    } = useContext(WorkingAppContext)
    const {
        modal,
        refresh_token,
        handleAction = NullAsDefault,
    } = useAnnouncementForm({
        owner_type: 'organization',
        ownerItem: activeOrganization,
        groups,
        defaultGroup: selectedDepartment,
        notifyListeners: {}
    })

    const keyword = useRouteSearchParam(filter_actions.keyword)
    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const refresh_deps = useMemo(
        () => [
            keyword,
            refresh_token,
            selectedDepartmentId,
            activeOrganization?.id,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [keyword, refresh_token,
            selectedDepartmentId,
            activeOrganization?.id, location.state]
    )


    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        isToggleDefault: true,
        AdvancedFilter: NullAsDefault,
        renderFilter: NullAsDefault,
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        noHeader: true,
        pagerInfo: {
            RenderEntity: React.Fragment,
            values: {
                owner_type: 'organization',
                owner_id: activeOrganization?.id,
                group_id: selectedDepartmentId,
            },
            PaginationMeta: NoneStickyMeta,
            apiInfo: announcement_gets_Api,
            renderItem: renderOrganizationAnnouncement({
                t,
                actionMenu: true,
                handleAction
            }),
            renderEmpty: () => (
                <div className="py-12 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-color-100">
                    <BsInboxesFill size={36} className="text-color-200"/>
                    <span className="text-sm text-color-400 italic tracking-wide">{t('no data is available now')}</span>
                </div>
            ),
            renderWrap: (child) => (
                <div className="flex flex-col gap-3">
                    {child}
                </div>
            ),
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onSortChange(sortBy)
            onFilterChange({
                keyword,
            })
        },
        filter_params: {
            keyword,
        },
        dependencies: [
            keyword,
        ],
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-3">
                {filterBar}
            </div>
        ),
    })

    return (
        <div className="min-h-screen antialiased">
            <DepartmentFilteredHeader
                title={title || 'announcements'}
                extra={!!activeOrganization?.edit && (
                    <Button
                        color="primary"
                        variant="filled"
                        style={{minHeight: 34, padding: '0 14px'}}
                        className="rounded-lg font-semibold text-xs flex-shrink-0"
                        onClick={() => handleAction(CRUD.CREATE)}>
                        {t('create')}
                    </Button>
                )}
            />
            <main className="mt-3 pb-8 flex flex-col gap-4">
                {activeOrganization && pager_widget}
            </main>
            {modal}
        </div>
    )
}

export default OrganizationAnnouncements
